import { Divider, message } from "antd";
import React from "react";
import { useEffect, useState } from "react";
import { createSearchParams, useNavigate, useParams } from "react-router-dom";
import CancelButton from "../../components/CancelButton";
import Head from "../../components/Head"
import Button from "../../components/UI/Button";
import Input from "../../components/UI/Input";
import Radio from "../../components/UI/Radio";
import SelectInput from "../../components/UI/SelectInput";
import SmartSearch from "../../components/UI/SmartSearch";
import { createOrUpdateCashier, getCashierById } from "../../services/cashiers";
import { notificationError } from "../../services/notification";
import cashiersStatus from "../../utils/cashiersStatus"
import { findRelativeStatus } from "../../utils/objectUtils";
import { InputMode } from "../../enums/inputTypeEnum";
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';

function NewCasher() {
	let { id } = useParams();
	const history = useNavigate()
	const statusSelectOptions = [
		{ label: cashiersStatus.active, value: cashiersStatus.active },
		{ label: cashiersStatus.inactive, value: cashiersStatus.inactive },
	]
	const enableOptions = [
		{ label: 'yes', value: 'yes' },
		{ label: 'no', value: 'no' }
	]
	const [form, setForm] = useState({
		locationId: "",
		login: "",
		password: "",
		name: "",
		status: cashiersStatus.active,
		canAccessPeriodSummary: enableOptions[0].value,
		canViewDrawerReport: enableOptions[0].value,
	})
	const [orig, setOrig] = useState({
		locationId: "",
		login: "",
		password: "",
		name: "",
		status: cashiersStatus.active,
		canAccessPeriodSummary: enableOptions[0].value,
		canViewDrawerReport: enableOptions[0].value,
	})
	const [location, setLocation] = useState('')
	const [validSearch, setValidSearch] = useState(false)
	const [showPassword, setShowPassword] = useState(false)

	useEffect(() => {
		if (id) {
			(async () => {
				const response = await getCashierById({ id })
				if (response && response.id) {
					setForm({
						locationId: response.locationId,
						login: response.login,
						password: "",
						name: response.name,
						status: findRelativeStatus(response.status, statusSelectOptions),
						canAccessPeriodSummary: response.canAccessPeriodSummary === "true" ? 'yes' : 'no',
						canViewDrawerReport: response.canViewDrawerReport === "true" ? 'yes' : 'no',
					})
					setOrig({
						locationId: response.locationId,
						login: response.login,
						password: "",
						name: response.name,
						status: findRelativeStatus(response.status, statusSelectOptions),
						canAccessPeriodSummary: response.canAccessPeriodSummary === "true" ? 'yes' : 'no',
						canViewDrawerReport: response.canViewDrawerReport === "true" ? 'yes' : 'no',
					})
					setLocation(response.locationName)
				}
			})()
		}
	}, [])

	const isChanged = () => {

		if(!form.locationId
			|| !form.login
			|| !form.name
			|| !form.status
		) return false
		if(!orig.locationId && !validSearch) return false
		if(form.locationId!==orig.locationId) return true
		if(form.login!==orig.login) return true
		if(form.name!==orig.name) return true
		if(form.status!==orig.status) return true
		if(form.canAccessPeriodSummary!==orig.canAccessPeriodSummary) return true
		if(form.canViewDrawerReport!==orig.canViewDrawerReport) return true
		return form.password !== orig.password;

	}
	const onFormChange = (val: string, name: string) => {
		setForm({
			...form,
			[name]: val
		})
	}

	const onFormSubmit = async () => {
		if (!form.name) return notificationError('You must provide a name')
		if (!form.login) return notificationError('You must provide login')
		if (!form.password && !id) return notificationError('You must provide a password')
		if ((!validSearch || !form.locationId) && !id) return notificationError('You must provide a valid location')
		let response = {success: true}
		if (!id) {
			response = await createOrUpdateCashier(form)
		}
		if (id) {
			response = await createOrUpdateCashier({
				id,
				locationId: form.locationId,
				login: form.login.trim(),
				password: form.password,
				name: form.name.trim(),
				status: form.status,
				canAccessPeriodSummary: form.canAccessPeriodSummary,
				canViewDrawerReport: form.canViewDrawerReport,
			})
		}
		if (!response.success){
			return
		}
		history({
			pathname:'/cashiers/',
			search: createSearchParams({
				useRecord: 'true'
			}).toString()
		})
	}

	return (
		<div className="new-cashier">
			<Head title={id ? 'Edit Casher' : 'New Cashier'} />
			<div className="new-cashier__container">
				<Divider orientation="left"><strong>Access</strong></Divider>
				<div className="new-cashier__container__row">
					<Input
						label="Name*"
						placeholder="Name "
						value={form.name}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFormChange(e.target.value, 'name')}
            innerProps={{id:'new-cashier-name'}}
					/>
					<Input
						label="Login*"
						placeholder="Login"
						value={form.login}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFormChange(e.target.value, 'login')}
            innerProps={{id:'new-cashier-login'}}
					/>
					<Input
						label="Password*"
						placeholder="Password "
						value={form.password}
						inputMode={showPassword? InputMode.TEXT: InputMode.PASSWORD}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFormChange(e.target.value, 'password')}
            innerProps={{id:'new-cashier-password'}}
					/>
					<Button style={{ width: 30, display: 'block'}}  onClick={() => setShowPassword((prevState) => !prevState)}>
						{!showPassword &&
							<EyeInvisibleOutlined />
						}
						{showPassword &&
							<EyeTwoTone />
						}
					</Button>
				</div>
				<Divider orientation="left"><strong>Actions</strong></Divider>
				<div className="new-cashier__container__row">
          <Radio
            innerProps={{id:'new-cashier-can-access-period-summary'}}
            label="Can Access Period Summary"
            options={enableOptions}
            value={form.canAccessPeriodSummary} 
            onChange={(e: any) => onFormChange(e.target.value, 'canAccessPeriodSummary')}
          />
          <Radio
            label="Can Edit Drawer Records"
            options={enableOptions}
            value={form.canViewDrawerReport} 
            onChange={(e: any) => onFormChange(e.target.value, 'canViewDrawerReport')}
            innerProps={{id:'new-cashier-can-view-drawer-report'}}
          />
				</div>
				<Divider orientation="left"><strong>About</strong></Divider>
				<div className="new-cashier__container__row">
          <Radio
						options={statusSelectOptions}
						label='Status'
						onChange={(e: any) => onFormChange(e.target.value, 'status')}
						value={form.status}
            innerProps={{id:'new-cashier-status'}}
          />
					{(!id || id==="") &&
					<SmartSearch
						label="Location *"
						searchBy={['location']}
						value={location}
						triggerWith={location}
						status="active"
						disabled={id ? true : false}
						onValidSearch={(e) => setValidSearch(e)}
						onObjValueChange={(e) => onFormChange(e.id, 'locationId')}
						onChange={(e) => setLocation(e)}
            innerProps={{id:'new-cashier-location'}}
					/>}
					{id && id!=="" &&
						<Input
							label="Location"
							placeholder="Location"
							value={location}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFormChange(e.target.value, 'location')}
							innerProps={{id:'new-cashier-location'}}
							disabled={true}
						/>
					}
				</div>
				<Divider></Divider>
				<div className="new-cashier__container__actions">
					<CancelButton customRedirect={() => {
						history({
							pathname:'/cashiers/',
							search: createSearchParams({
								useRecord: 'true'
							}).toString()
						})
					}}/>
					<Button type="primary"
							onClick={onFormSubmit}
							disabled={!isChanged()}
					>
						<p id="save-button">Save</p>
					</Button>
				</div>
			</div>
		</div>
	)
}

export default NewCasher
